import React, { useState } from "react";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import { MENTOR_TAG_LABELS } from "../MentorAssignPanel/constants";
import styles from "./MentorCard.module.css";

/* The line every chosen mentor carries in the design. */
export const ASSIGNED_NOTE = "This mentor will be assigned directly to the batch";

/**
 * The mentor's photo, or their initials when there is none or it fails to load.
 * A bare <img> whose src 404s renders the browser's broken-image glyph and the
 * alt text, which is how a missing photo used to look on this card.
 */
const MentorAvatar = ({ name, url, size }) => {
  /* The url that failed, not a boolean: the panel and the read-only section both
     reuse this card for whichever mentor they show next, so a flag would leave a
     later mentor on the initials fallback even when their photo is fine. */
  const [failedUrl, setFailedUrl] = useState(null);
  const initials = (name ?? "")
    ?.trim?.()
    ?.split?.(/\s+/)
    ?.slice?.(0, 2)
    ?.map?.((word) => word?.charAt?.(0) ?? "")
    ?.join?.("")
    ?.toUpperCase?.();
  const sizeClass = size === "large" ? styles.avatarLarge : styles.avatar;

  if (!url || failedUrl === url) {
    return (
      <span className={`${sizeClass} ${styles.avatarFallback}`} aria-hidden="true">
        {initials}
      </span>
    );
  }

  /* alt="" on purpose: the name is rendered right beside it. */
  return <img className={sizeClass} src={url} alt="" onError={() => setFailedUrl(url)} />;
};

/**
 * Name, the group tag and availability; "Occupied" carries its reasons on hover.
 * The read-only current mentor shows no availability — the design's card there has
 * the tag alone.
 */
const NameLine = ({ mentor, showStatus = true }) => {
  const isOccupied = mentor?.status === "occupied";
  return (
    <span className={styles.nameLine}>
      <span className={styles.name}>{mentor?.name}</span>
      {MENTOR_TAG_LABELS?.[mentor?.tag] && (
        <span className={styles.tag}>{MENTOR_TAG_LABELS?.[mentor?.tag]}</span>
      )}
      {showStatus && mentor?.status === "available" && <span className={styles.available}>Available</span>}
      {showStatus && isOccupied && (
        <span className={styles.occupied} tabIndex={0}>
          Occupied
          <SvgIcons name="info_circle" width="16" height="16" />
          {mentor?.occupiedTimings?.length > 0 && (
            <span className={styles.tooltip} role="tooltip">
              {mentor?.occupiedTimings?.map((timing) => (
                <span key={`${timing?.day}-${timing?.from}`} className={styles.tooltipLine}>
                  {timing?.dayLabel} {timing?.fromLabel ?? timing?.from} IST: {timing?.reasonLabel}
                </span>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

/** Rating and shift, as the design's meta line shows them. */
const MetaLine = ({ mentor }) => (
  <span className={styles.metaLine}>
    <span className={styles.meta}>
      <SvgIcons name="star_filled" width="16" height="16" />
      {mentor?.rating ?? "Not rated"}
    </span>
    {mentor?.shift && (
      <span className={styles.meta}>
        <SvgIcons name="video_icon" width="16" height="16" />
        {mentor?.shift}
      </span>
    )}
  </span>
);

/** What still stands against a free mentor: calendar conflicts and leave. */
const Warnings = ({ mentor }) =>
  mentor?.conflictCount > 0 || mentor?.leaves?.length > 0 ? (
    <ul className={styles.notes}>
      {mentor?.conflictCount > 0 && (
        <li className={styles.noteWarning}>
          {mentor?.conflictCount} calendar conflict{mentor?.conflictCount > 1 ? "s" : ""} at these
          times. You&rsquo;ll see them when you add the batch.
        </li>
      )}
      {mentor?.leaves?.map((leave) => (
        <li key={`${leave?.from}-${leave?.to}`} className={styles.noteWarning}>
          On leave {leave?.from} to {leave?.to} (IST)
        </li>
      ))}
    </ul>
  ) : null;

/** The mentor's open slots per weekday, collapsed until asked for (R-05-09). */
const SlotAvailability = ({ slots }) => {
  const [open, setOpen] = useState(false);
  if (!slots?.length) return null;
  return (
    <div className={styles.slotsWrap}>
      <button
        type="button"
        className={styles.slotsHeader}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        Slot availability
        <SvgIcons
          name="chevron_down"
          className={open ? styles.chevronOpen : undefined}
          width="20"
          height="20"
        />
      </button>
      {open && (
        <div className={styles.slotRows}>
          {slots?.map((slotDay) => (
            <div key={slotDay?.day} className={styles.slotRow}>
              <span className={styles.slotDay}>{slotDay?.dayLabel}</span>
              <span className={styles.slotChips}>
                {slotDay?.times?.map((time) => (
                  <span key={time?.primary} className={styles.slotChip}>
                    <span className={styles.slotPrimary}>{time?.primary}</span>
                    {time?.secondary && <span className={styles.slotSecondary}>{time?.secondary}</span>}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProfileLink = ({ url }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className={styles.profileLink}>
      View profile
      <SvgIcons name="external_link" width="16" height="16" />
    </a>
  ) : null;

/**
 * A mentor card (R-05-08), in the two shapes the design draws.
 *
 * `option` — the demo / current / past mentor as the first way to assign. Free,
 * it carries a "Select" button that turns into "Selected", and once assigned the
 * design's one line saying what happens on save. Occupied, it is greyed with a
 * disabled radio, an "Occupied" tag whose tooltip names each blocked class, and
 * the slot grid so the schedule can be moved onto a slot the mentor has open.
 *
 * `current` — the batch's mentor on Edit, read-only, with a link to the profile;
 * `muted` greys it while the schedule has changed and a new list is due.
 *
 * @param {{ mentor: import("../services/mentorMapper").MentorVM,
 *           variant?: "option"|"current", selected?: boolean, assigned?: boolean,
 *           onSelect?: () => void, disabled?: boolean, muted?: boolean }} props
 */
const MentorCard = ({ mentor, variant = "current", selected, assigned, onSelect, disabled, muted }) => {
  if (variant === "current") {
    return (
      <div className={muted ? styles.currentMuted : styles.current}>
        <MentorAvatar name={mentor?.name} url={mentor?.avatarUrl} />
        <span className={styles.text}>
          <NameLine mentor={mentor} showStatus={false} />
          <MetaLine mentor={mentor} />
          <ProfileLink url={mentor?.profileUrl} />
        </span>
      </div>
    );
  }

  if (mentor?.status === "occupied") {
    return (
      <div className={styles.occupiedCard}>
        <label className={styles.occupiedHeader}>
          <input type="radio" className={styles.radio} checked={false} disabled readOnly />
          <MentorAvatar name={mentor?.name} url={mentor?.avatarUrl} />
          <span className={styles.textTight}>
            <NameLine mentor={mentor} />
            <MetaLine mentor={mentor} />
          </span>
        </label>
        <SlotAvailability slots={mentor?.slots} />
      </div>
    );
  }

  return (
    <div className={selected ? styles.optionSelected : styles.option}>
      <div className={styles.optionHeader}>
        <MentorAvatar name={mentor?.name} url={mentor?.avatarUrl} size="large" />
        <span className={styles.text}>
          <NameLine mentor={mentor} />
          <MetaLine mentor={mentor} />
          {/* The design's Edit card links the profile under the meta line; kept
              here too, since this card is the only way to reach it on Add. */}
          <ProfileLink url={mentor?.profileUrl} />
        </span>
        {selected ? (
          <span className={styles.selectedBadge}>
            <SvgIcons name="check_circle_filled" width="16" height="16" />
            Selected
          </span>
        ) : (
          <button type="button" className={styles.selectButton} disabled={disabled} onClick={onSelect}>
            Select
          </button>
        )}
      </div>
      <Warnings mentor={mentor} />
      {selected && assigned && <p className={styles.assignedNote}>{ASSIGNED_NOTE}</p>}
    </div>
  );
};

export default MentorCard;
